import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogoWordmark } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(username, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate("/app/dashboard");
    } else {
      setError(res.error);
      toast.error(res.error);
    }
  };

  const quick = null;

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#0A192F]">
      {/* left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(600px circle at 20% 20%, #0F4C81, transparent), radial-gradient(500px circle at 80% 80%, #0B6E4F, transparent)" }} />
        <div className="relative"><LogoWordmark light /></div>
        <div className="relative">
          <h2 className="text-4xl font-extrabold text-white leading-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
            Fuel every loan lead<br /><span className="text-emerald-400">from mela to disbursal.</span>
          </h2>
          <p className="text-slate-300 mt-4 max-w-md">
            The command centre for your relationship managers — customers, follow-ups, loan melas and live conversion analytics in one place.
          </p>
        </div>
        <div className="relative flex items-center gap-2 text-slate-400 text-sm">
          <ShieldCheck size={16} className="text-emerald-400" /> Bank-grade JWT security
        </div>
      </div>

      {/* right form */}
      <div className="flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center"><LogoWordmark /></div>
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
            <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Sign in</h1>
            <p className="text-sm text-slate-500 mt-1 mb-6">Use your username & password to access the portal.</p>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="username">Username or Email</Label>
                <Input id="username" data-testid="login-username" value={username} onChange={(e) => setUsername(e.target.value)} placeholder="admin" className="mt-1.5" autoFocus />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-1.5" />
              </div>
              {error && <div className="text-sm text-rose-600 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
              <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-[#0F4C81] hover:bg-[#0A3A66] h-11 text-base font-semibold">
                {loading ? <Loader2 className="animate-spin" size={18} /> : "Sign In"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
